import React, { useState, useEffect } from 'react';
import { Package, ShoppingCart, DollarSign, TrendingUp, Plus, Eye, AlertCircle, Users, Clock, CheckCircle, XCircle } from 'lucide-react';
import { sellersApi } from '@/services/api';
import { useAuth } from '@/store/authStore';
import Button from '@/components/ui/Button';
import { useNavigate } from 'react-router-dom';




const SellerDashboardPage: React.FC = () => {
    const { user } = useAuth();
    const [dashboardData, setDashboardData] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const [error, setError] = useState<string | null>(null);//error general
    useEffect(() => {
        // Aquí cargarías los datos reales del dashboard
        const fetchDashboardData = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const data = await sellersApi.getSellerDashboard();
                //console.log('Dashboard data:', data);
                setDashboardData(data);

                // Simulación de carga
                //await new Promise(resolve => setTimeout(resolve, 1000));
            } catch (error) {
                //console.error('Error loading dashboard:', error);
                setError('Error al cargar los datos del dashboard');
            } finally {
                setIsLoading(false);
            }
        };

        fetchDashboardData();
    }, []);

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'pending':
                return 'bg-yellow-100 text-yellow-800';
            case 'shipped':
                return 'bg-blue-100 text-blue-800';
            case 'delivered':
                return 'bg-green-100 text-green-800';
            case 'cancelled':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'pending':
                return <Clock className="w-4 h-4" />;
            case 'shipped':
                return <Package className="w-4 h-4" />;
            case 'delivered':
                return <CheckCircle className="w-4 h-4" />;
            case 'cancelled':
                return <XCircle className="w-4 h-4" />;
            default:
                return <AlertCircle className="w-4 h-4" />;
        }
    };

    const formatCurrency = (amount: number | undefined) => {
        if (!amount) return '0.00';
        return new Intl.NumberFormat('es-PE', {
            style: 'currency',
            currency: 'PEN'
        }).format(amount);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('es-PE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Cargando dashboard...</p>
                </div>
            </div>
        );
    }
    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center p-4 bg-red-50 rounded-lg max-w-md">
                    <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-red-800 mb-2">Error</h3>
                    <p className="text-red-600 mb-4">{error}</p>
                    <Button variant="primary" onClick={() => window.location.reload()}>
                        Reintentar
                    </Button>
                </div>
            </div>
        );
    }
    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">
                                Panel de Vendedor
                            </h1>
                            <p className="mt-1 text-gray-600">
                                Bienvenido de vuelta, {user?.first_name + ' ' + user?.last_name}
                            </p>
                        </div>
                        <div className="mt-4 sm:mt-0 flex space-x-3" >
                            <Button variant="secondary"
                                leftIcon={<Eye className="w-4 h-4" />}
                                //onClick={() => navigate(`/seller/store/${user?.seller_id}`)} >
                                onClick={() => navigate('/seller/profile')} >
                                Ver Tienda
                            </Button>
                            <Button
                                leftIcon={<Plus className="w-4 h-4" />}
                                onClick={() => navigate('/seller/products/new')}
                                variant="primary"
                            >
                                Nuevo Producto
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center">
                            <div className="p-2 bg-blue-100 rounded-lg">
                                <Package className="w-6 h-6 text-blue-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Productos</p>
                                <p className="text-2xl font-bold text-gray-900">
                                    {dashboardData?.product_summary?.total_products}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center">
                            <div className="p-2 bg-green-100 rounded-lg">
                                <ShoppingCart className="w-6 h-6 text-green-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Órdenes</p>
                                <p className="text-2xl font-bold text-gray-900">
                                    {dashboardData?.sales_summary?.total_orders}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center">
                            <div className="p-2 bg-yellow-100 rounded-lg">
                                <DollarSign className="w-6 h-6 text-yellow-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Ventas Totales</p>
                                <p className="text-2xl font-bold text-gray-900">
                                    {formatCurrency(dashboardData?.stats?.total_sales)}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center">
                            <div className="p-2 bg-red-100 rounded-lg">
                                <Clock className="w-6 h-6 text-red-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Pendientes</p>
                                <p className="text-2xl font-bold text-gray-900">
                                    {dashboardData?.sales_summary?.pending_orders}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Recent Orders */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <h2 className="text-lg font-semibold text-gray-900">
                                    Órdenes Recientes
                                </h2>
                                <button className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                                    onClick={() => navigate('/seller/orders')}>
                                    Ver todas
                                </button>
                            </div>
                        </div>
                        <div className="p-6">
                            <div className="space-y-4">
                                {dashboardData?.recent_orders?.map((order: any) => (
                                    <div key={order.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-3">
                                                <div>
                                                    <p className="font-medium text-gray-900">{order.order_number}</p>
                                                    <p className="text-sm text-gray-600">{order.customer_name}</p>
                                                </div>
                                            </div>
                                            <p className="text-xs text-gray-500 mt-1">
                                                {formatDate(order.created_at)}
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <p className="font-semibold text-gray-900">
                                                {formatCurrency(order.total)}
                                            </p>
                                            <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                                                {getStatusIcon(order.status)}
                                                <span className="capitalize">{order.status}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Top Products */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <h2 className="text-lg font-semibold text-gray-900">
                                    Productos Más Vendidos
                                </h2>
                                <button className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                                    onClick={() => navigate('/seller/products')}>
                                    Ver todos
                                </button>
                            </div>
                        </div>
                        <div className="p-6">
                            <div className="space-y-4">
                                {dashboardData?.top_products?.map((product: any, index: number) => (
                                    <div key={product.id} className="flex items-center space-x-4">
                                        <div className="flex items-center justify-center w-8 h-8 bg-primary-100 text-primary-600 rounded-full text-sm font-bold">
                                            {index + 1}
                                        </div>
                                        <div className="w-12 h-12 bg-gray-200 rounded-lg overflow-hidden">
                                            <img
                                                src={product.image}
                                                alt={product.name}
                                                className="w-full h-full object-cover"
                                                onError={(e) => {
                                                    (e.target as HTMLImageElement).src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yMCAyOEMyNC40MTgzIDI4IDI4IDI0LjQxODMgMjggMjBDMjggMTUuNTgxNyAyNC40MTgzIDEyIDIwIDEyQzE1LjU4MTcgMTIgMTIgMTUuNTgxNyAxMiAyMEMxMiAyNC40MTgzIDE1LjU4MTcgMjggMjAgMjhaIiBmaWxsPSIjOUI5QkEzIi8+Cjwvc3ZnPgo=';
                                                }}
                                            />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-gray-900 truncate">
                                                {product.name}
                                            </p>
                                            <p className="text-sm text-gray-600">
                                                {product.total_sales} vendidos
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <p className="font-semibold text-gray-900">
                                                {formatCurrency(product.revenue)}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="mt-8 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">
                        Acciones Rápidas
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <button
                            className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                            onClick={() => navigate('/seller/products/new')}
                        >
                            <Plus className="w-8 h-8 text-primary-600 mb-2" />
                            <span className="text-sm font-medium text-gray-700">Nuevo Producto</span>
                        </button>
                        <button
                            className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                            onClick={() => navigate('/seller/products')}
                        >
                            <Package className="w-8 h-8 text-blue-600 mb-2" />
                            <span className="text-sm font-medium text-gray-700">Gestionar Inventario</span>
                        </button>
                        <button
                            className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                            onClick={() => navigate('/seller/reports')}
                        >
                            <TrendingUp className="w-8 h-8 text-green-600 mb-2" />
                            <span className="text-sm font-medium text-gray-700">Reportes</span>
                        </button>
                        <button
                            className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                            onClick={() => navigate('/seller/customers')}
                        >
                            <Users className="w-8 h-8 text-purple-600 mb-2" />
                            <span className="text-sm font-medium text-gray-700">Clientes</span>
                        </button>
                    </div>
                </div>
            </div>
        </div >
    );
};

export default SellerDashboardPage;